/**
 * Validations de saisie côté interface : elles préviennent avant l'envoi ce que
 * l'API refuserait de toute façon (ou, pour le téléphone et la TVA, ce qu'elle
 * accepte sans le vérifier). Chaque fonction rend `null` (saisie acceptable —
 * le vide aussi, les champs concernés sont facultatifs) ou la clé i18n du
 * message, à passer à `t()`.
 */

/**
 * Longueurs maximales des champs de saisie, reprises des schémas de l'API
 * (`apps/api/src/{contacts,artistes,oeuvres,expositions,lieux}`). Pas de
 * constante partagée côté `@artosera/shared` : les schémas d'entrée vivent
 * dans les routes. À tenir d'accord avec elles.
 */
export const LIMITES = {
  contact: { nom: 200, prenom: 120, raisonSociale: 200, email: 200, telephone: 40, tva: 40 },
  artiste: { nom: 200, naissance: 120 },
  oeuvre: { titre: 300, numeroInventaire: 60 },
  exposition: { stand: 40 },
  lieu: { nom: 200 },
} as const;

export type CleErreurSaisie =
  | 'regie:validation.telephone'
  | 'regie:validation.tvaIntracom'
  | 'regie:validation.annee'
  | 'regie:validation.dimension'
  | 'regie:validation.email';

const TELEPHONE = /^\+?[\d\s().\-/]+$/;

export function erreurTelephone(saisie: string): CleErreurSaisie | null {
  const valeur = saisie.trim();
  if (valeur === '') return null;
  const chiffres = valeur.replace(/\D/g, '').length;
  const plusIllegal = valeur.slice(1).includes('+');
  if (!TELEPHONE.test(valeur) || plusIllegal || chiffres < 6 || chiffres > 15) {
    return 'regie:validation.telephone';
  }
  return null;
}

/** Numéro de TVA intracommunautaire : code pays à deux lettres, puis 2 à 12 caractères. */
export function erreurTvaIntracom(saisie: string): CleErreurSaisie | null {
  const valeur = saisie.replace(/[\s.]/g, '').toUpperCase();
  if (valeur === '') return null;
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(valeur) ? null : 'regie:validation.tvaIntracom';
}

/** Année : entier de 0 à 2999 (le schéma de l'API). */
export function erreurAnnee(saisie: string): CleErreurSaisie | null {
  const valeur = saisie.trim();
  if (valeur === '') return null;
  if (!/^\d{1,4}$/.test(valeur)) return 'regie:validation.annee';
  return Number(valeur) <= 2999 ? null : 'regie:validation.annee';
}

/** Dimension en centimètres (ou dans l'unité choisie) : un nombre de 0 à 100 000. */
export function erreurDimension(saisie: string): CleErreurSaisie | null {
  const valeur = saisie.trim().replace(',', '.');
  if (valeur === '') return null;
  if (!/^\d+(?:\.\d+)?$/.test(valeur)) return 'regie:validation.dimension';
  return Number(valeur) <= 100_000 ? null : 'regie:validation.dimension';
}

export function erreurEmail(saisie: string): CleErreurSaisie | null {
  const valeur = saisie.trim();
  if (valeur === '') return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valeur) ? null : 'regie:validation.email';
}
